import React, { useState, useEffect, useRef } from 'react'
import ReactDOM from 'react-dom'
import Chevron from '../../assets/icons/Chevron'
import { CloseIcon } from '../../assets/icons/CloseIcon'
import { Flex, InvalidText } from 'styling/GlobalStyles'
import { isEmptyArray } from 'utils/helpers'
import { toast } from 'react-toastify'
import { ISelectValue } from 'constants/globalTypes'
import { BodyText } from 'styling/Typography'
import { MultiSingle, OptionsContainer, Placeholder, Select, SelectOption } from './HonestSelect.styled'

export const HonestSelect = ({
  options,
  onChange,
  width = 'auto',
  defaultValue,
  multi,
  name,
  placeholder,
  api,
  invalid,
  editingMultiNone,
  optionsForDefault,
  isNone,
  menuTop,
  errorWhenEmpty,
  experimentalResetValue,
  fixedBottomOptionsContainer,
  disabled,
  blured,
  openOptions,
  setOpenOptions,
  onBlur,
  noX = false,

}: IHonestSelect) => {

  const [selectedOption, setSelectedOption] = useState<ISelectValue>(
    defaultValue && typeof defaultValue == 'string' && defaultValue.length > 0
      ? optionsForDefault
        ? optionsForDefault.find((item) => item.value.toLowerCase() === defaultValue.toLowerCase())
        : options.find((item) => item.value.toLowerCase() === defaultValue.toLowerCase())
      : typeof defaultValue == 'number'
        ? options?.find((item) => item.value === defaultValue)
        : null,
  )
  const [selectedMulti, setSelectedMulti] = useState(
    multi && defaultValue
      ? defaultValue.map((item) => (typeof item == 'string' ? { label: item.toUpperCase(), value: item.toLowerCase() } : item)) // TODO
      : [],
  )
  const [editableOptions, setEditableOptions] = useState(options)
  const [none, setNone] = useState(editingMultiNone ? editingMultiNone : isNone ?? false)

  const filterItemString = (list) =>
    list.find((item) => item.value.toLowerCase() === defaultValue.toLowerCase())
  const filterItemNumber = (list) => list?.find((item) => item.value === defaultValue)

  useEffect(() => {
    if (multi && defaultValue) {
      setEditableOptions(options.filter((x) => !defaultValue.includes(x.value)))
    }
  }, [])

  const handleEnter = (event) => {
    if (event.key.toLowerCase() === 'enter') {
      event.preventDefault()
      event.target.parentNode.nextSibling.children[1].focus()
    }
  }

  const ref = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState({
    top: 0,
    left: 0,
    width: 0
  })

  useEffect(() => {
    const updatePosition = () => {
      if (!ref.current || !openOptions) return

      const rect = ref.current.getBoundingClientRect()
      const menuHeight = Math.min(options.length * 40, 250)
      const shouldShowAbove = menuTop || (rect.bottom + menuHeight > window.innerHeight && rect.top > menuHeight)

      setPosition({
        top: shouldShowAbove ? rect.top - menuHeight : rect.bottom,
        left: rect.left,
        width: rect.width
      })
    }

    updatePosition()
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)

    return () => {
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [openOptions, options.length, menuTop])

  useEffect(() => {
    if (
      selectedOption &&
      !optionsForDefault &&
      !options.some((option) => option.value == selectedOption.value)
    ) {
      setSelectedOption(null)
    }
    const filterList = (list) => list.filter((x) => !defaultValue.includes(x.value))
    setEditableOptions(
      multi && defaultValue ? filterList(optionsForDefault ? optionsForDefault : options) : options,
    )
  }, [options])

  useEffect(() => {
    if (experimentalResetValue) {
      if (!defaultValue) {
        setSelectedOption(null)
      }
    }

  }, [defaultValue])


  useEffect(() => {
    if (api) {
      if (defaultValue && typeof defaultValue == 'string') {
        setSelectedOption(filterItemString(optionsForDefault ? optionsForDefault : options))
      } else {
        setSelectedOption(
          filterItemNumber(!isEmptyArray(optionsForDefault) ? optionsForDefault : options),
        )
      }
    }
  }, [options])

  useEffect(() => {
    if (defaultValue && typeof defaultValue == 'string') {
      setSelectedOption(filterItemString(optionsForDefault ? optionsForDefault : options))
    } else if (defaultValue && typeof defaultValue == 'number') {
      setSelectedOption(
        filterItemNumber(!isEmptyArray(optionsForDefault) ? optionsForDefault : options),
      )
    }
    if (multi && defaultValue && selectedMulti.length == 0) {
      setSelectedMulti(
        defaultValue.map((item) => typeof item == 'string' ? ({ label: item.toUpperCase(), value: item.toLowerCase() }) : item),
      )
    }
  }, [defaultValue])

  const handleChange = (item) => {
    if (multi) {
      defaultValue = null
      const isNone = item.label === 'None'

      if (isNone) {
        setEditableOptions(() => {
          return options
        })
        setSelectedMulti([])
        setNone(true)
        onChange({ label: 'multiNone', value: null }, name)
        return
      }
      setEditableOptions((prev) => prev.filter((it) => it.value !== item.value))

      setSelectedMulti((prev) => {
        const isFound = prev.some((element) => element.value === item.value)
        setNone(false)
        const filtered = prev.filter((i) => i.value !== item.value)
        const noLicense = item.value === null
        const forReturn = isFound ? filtered : noLicense ? null : isNone ? [] : [...prev, item]

        if ((forReturn.length > 0 || isNone) && onChange && multi && forReturn) {
          onChange(forReturn, name)
        }
        return forReturn
      })
    } else {
      setSelectedOption(item)
      onChange && item && item !== null ? onChange(item, name) : null
    }
  }

  const handleRemove = (item) => {
    setEditableOptions((prev) => [...prev, item])
    setSelectedMulti((prev) => prev.filter((it) => it !== item))
    onChange(selectedMulti.filter((it) => it !== item), name)
    defaultValue = null
  }

  const clearSelect = (e) => {
    e.stopPropagation()
    if (multi) {
      setSelectedMulti([])
      setEditableOptions(options)
      setNone(false)
      setOpenOptions(false)
      defaultValue = null
      onChange([], name)
      return
    }
    onChange({ value: null, label: '' }, name)
    setSelectedOption(null)
    setOpenOptions(false)
    defaultValue = null
  }

  const handleOpen = () => {
    if (errorWhenEmpty && !options.length) {
      toast.error(errorWhenEmpty)
      return
    }
    setOpenOptions((prev) => !prev)
  }

  const renderDropdown = () => {
    if (!openOptions) return null

    const dropdownStyle: React.CSSProperties = {
      position: 'fixed',
      top: `${position.top}px`,
      left: `${position.left}px`,
      width: `${position.width}px`,
      maxHeight: '250px',
      overflowY: 'auto',
      zIndex: 1000,
    }

    if (fixedBottomOptionsContainer) {

      const { x, y, width } = ref.current.getBoundingClientRect()

      return <OptionsContainer
        style={{
          top: y - 40,
          left: x - 80,
          width,
          position: 'fixed',
          zIndex: 999999999999999
        }}
        onClick={e => e.stopPropagation()}
      >
        {editableOptions.map((item, key) => (
          <SelectOption onClick={() => handleChange(item)} key={key}>
            <BodyText>{item.label}</BodyText>
          </SelectOption>
        ))}
      </OptionsContainer>
    }

    return ReactDOM.createPortal(
      <OptionsContainer
        style={dropdownStyle}
        onClick={e => e.stopPropagation()}
      >
        {editableOptions.map((item, key) => (
          <SelectOption onClick={() => handleChange(item)} key={key}>
            <BodyText>{item.label}</BodyText>
          </SelectOption>
        ))}
      </OptionsContainer>,
      document.body
    )
  }

  const blockInteraction: any = disabled ? {
    userSelect: 'none',
    pointerEvents: 'none',
    opacity: 0.7
  } : {}


  return (
    <Select
      ref={ref}
      tabIndex={0}
      onBlur={onBlur}
      onClick={handleOpen}
      style={{
        width,
        minWidth: width,
        maxWidth: width,
        ...blockInteraction
      }}
      onKeyDown={handleEnter}
      value={selectedOption ? selectedOption.value : multi ? (selectedMulti && selectedMulti.length > 0 ? selectedMulti.map(x => x.value).join(',') : '') : ''}
      invalid={invalid}
      active={openOptions}
      blured={blured}

    >
      <Flex style={{ flex: 1 }} >

        {multi ? (
          selectedMulti && selectedMulti.length > 0 ? (
            <div className='multi-container'>
              {selectedMulti.map((item, key) => (
                <MultiSingle key={key} className='multi-single'>
                  <BodyText>{item.label}</BodyText>
                  <div onClick={() => handleRemove(item)} className='close-container'>
                    <CloseIcon />
                  </div>
                </MultiSingle>
              ))}
            </div>
          ) : (
            <Placeholder>{none ? 'None' : 'Select'}</Placeholder>
          )
        ) : (
          <Placeholder style={selectedOption ? { color: '#fff' } : {}}>
            {selectedOption ? (
              selectedOption.label
            ) : placeholder ? (
              placeholder
            ) : (
              'Select'
            )}
          </Placeholder>
        )}
      </Flex>

      <Flex>
        <Chevron />
        {!noX &&
        (selectedOption?.value ||
          selectedOption?.value === 0 ||
          (multi && selectedMulti && selectedMulti.length > 0)) ? (
          <CloseIcon onClick={clearSelect} />
        ) : null}
      </Flex>

      {renderDropdown()}
      <article>{invalid && blured && <InvalidText>This field is required</InvalidText>}</article>
    </Select>
  )
}


interface IHonestSelect {
  onChange: (value: ISelectValue | any[], name: string | number) => void
  options: any[]
  width?: string
  defaultValue?: any
  multi?: boolean
  multiOptions?: any[]
  name?: number | string
  placeholder?: string
  api?: boolean
  invalid?: boolean
  editingMultiNone?: boolean
  optionsForDefault?: any[]
  noX?: boolean
  isNone?: boolean
  menuTop?: boolean
  errorWhenEmpty?: string
  experimentalResetValue?: boolean
  disabled?: boolean
  fixedBottomOptionsContainer?: boolean
  blured?: boolean,
  setBlured?: React.Dispatch<React.SetStateAction<boolean>>,
  openOptions?: boolean,
  setOpenOptions?: React.Dispatch<React.SetStateAction<boolean>>
  onBlur?: () => void
}